import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItem,
  ListItemText,
  List,
  Stack,
  Tabs,
  Tab
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StarIcon from "@mui/icons-material/Star";
import BarChartIcon from "@mui/icons-material/BarChart";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import HeaderRec from "../../header/headerRec";
import PaginatedList from "./PaginatedList";
import "./StatistiquesPage.css";
import Footer from "../../commun/Footer";
import LoadingPopup from "../../commun/LoadingPopup";

const MultiDeviseAmount = ({ montantObj }) => (
  <Box className="multi-devise-amount">
    {montantObj &&
      Object.entries(montantObj).map(
        ([devise, montant]) =>
          montant > 0 && (
            <Typography key={devise} variant="h6" className="montant-texte">
              {Number(montant).toLocaleString()} {devise}
            </Typography>
          )
      )}
  </Box>
);


const StatistiquesPage = () => {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const [activeTab, setActiveTab] = useState("candidature");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_BASE}/api/recruteur-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setStats(data);
    };
    fetchStats();
  }, [API_BASE]);

  if (!stats)
    return <LoadingPopup open={!stats} message="Chargement..." />;

  return (
    <Box>
      <HeaderRec />
      <Box className="container-vertical">
        {/* Titre principal */}
        <Typography variant="h3" align="center" gutterBottom className="main-title">
          Mes statistiques détaillées
        </Typography>
        <Divider className="section-divider" />

        {/* === TWO-COLUMN LAYOUT === */}
        <Box
          sx={{
            mt: 1,
            display: 'grid',
            // Left rail: fixed/comfortable width; Right rail: takes the rest
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 320px) minmax(0, 1fr)' },
            gap: 2,
          }}
        >
          {/* LEFT COLUMN: KPI CARDS */}
          <Box
            sx={{
              alignSelf: 'start',
              position: { md: 'sticky' },
              top: { md: 16 },
              minWidth: 0,
            }}
          >
            <Stack spacing={2}>
              {/* Niveau */}
              <Card className="card-section niveau-bg">
                <CardContent className="card-content-icon">
                  <Box className="icon-box niveau-icon"><StarIcon fontSize="large" /></Box>
                  <Box className="card-main-content">
                    <Typography variant="subtitle1" className="section-title">Niveau actuel</Typography>
                    <Typography variant="h6" color="primary" className="niveau-label">
                      {stats.niveau?.label || "Débutant"}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              {/* Ma cagnotte */}
              <Card className="card-section cagnotte-bg">
                <CardContent className="card-content-icon">
                  <Box className="icon-box cagnotte-icon"><AccountBalanceWalletIcon fontSize="large" /></Box>
                  <Box className="card-main-content">
                    <Typography variant="subtitle1" className="section-title">Ma cagnotte</Typography>
                    <MultiDeviseAmount montantObj={stats.cagnotte} />
                  </Box>
                </CardContent>
              </Card>

              {/* Prime estimée */}
              <Card className="card-section prime-bg">
                <CardContent className="card-content-icon">
                  <Box className="icon-box prime-icon"><MonetizationOnIcon fontSize="large" /></Box>
                  <Box className="card-main-content">
                    <Typography variant="subtitle1" className="section-title">Gains potentiels</Typography>
                    <MultiDeviseAmount montantObj={stats.primeTotale} />
                  </Box>
                </CardContent>
              </Card>
            </Stack>
          </Box>

          {/* RIGHT COLUMN: MAIN STATS/ACCORDIONS */}
          <Box sx={{ minWidth: 0 }}>
            <Card className="card-section stats-bg">
              <CardContent className="card-content-icon">
                <Box className="icon-box stats-icon"><BarChartIcon fontSize="large" /></Box>
                <Box className="card-main-content wide">
                  <Typography variant="subtitle1" className="section-title" gutterBottom>
                    Statistiques principales
                  </Typography>

                  {/* Navigation Tabs */}
                  <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    textColor="primary"
                    indicatorColor="primary"
                    sx={{ mb: 2 }}
                  >
                    <Tab value="candidature" label="Candidature" />
                    <Tab value="mission" label="Mission" />
                    <Tab value="talent" label="Talent" />
                    <Tab value="resume" label="Résumé" />
                  </Tabs>

                  {/* TAB CONTENT */}
                  <Box sx={{ mt: 2 }}>
                    {activeTab === "candidature" && (
                      <Grid container spacing={2}>
                        {/* Moyenne candidatures par mission */}
                        <Grid container spacing={2} alignItems="stretch">
                          <Grid item xs={12} md={4}>
                            <Card sx={{ borderRadius: 3, boxShadow: 3, height: "100%" }}>
                              <CardContent>
                                <Typography variant="h6">📌 Candidatures / Mission</Typography>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="body1">
                                  <b>{(stats.kpis.candidaturesStats.moyenneParMission ?? 0).toFixed(2)}</b>
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>

                        {/* Taux acceptation / refus */}
                        <Grid container spacing={2} alignItems="stretch">
                          <Grid item xs={12} md={4}>
                            <Card sx={{ borderRadius: 3, boxShadow: 3, height: "100%" }}>
                              <CardContent>
                                <Typography variant="h6">📊 Taux candidatures</Typography>
                                <Divider sx={{ my: 1 }} />
                                <Typography>✅ Acceptation : <b>{(stats.kpis.candidaturesStats.tauxAcceptation ?? 0).toFixed(1)}%</b></Typography>
                                <Typography>❌ Refus : <b>{(stats.kpis.candidaturesStats.tauxRefus ?? 0).toFixed(1)}%</b></Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>

                        {/* Tarif moyen demandé */}
                        <Grid container spacing={2} alignItems="stretch">
                          <Grid item xs={12} md={4}>
                            <Card sx={{ borderRadius: 3, boxShadow: 3, height: "100%" }}>
                              <CardContent>
                                <Typography variant="h6">💶 Tarif moyen demandé</Typography>
                                <Divider sx={{ my: 1 }} />

                                {stats.kpis.candidaturesStats.tarifMoyen &&
                                  Object.entries(stats.kpis.candidaturesStats.tarifMoyen).length > 0 ? (
                                  Object.entries(stats.kpis.candidaturesStats.tarifMoyen).map(([devise, data]) => (
                                    <Typography key={devise}>
                                      {devise} : <b>{data.moyenne.toFixed(0)}</b> ({data.nombreCandidatures} candidatures)
                                    </Typography>
                                  ))
                                ) : (
                                  <Typography variant="body2">Aucune donnée disponible.</Typography>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>
                      </Grid>
                    )}

                    {activeTab === "mission" && (
                      <Grid container spacing={2}>
                        {/* Total missions */}
                        <Grid item xs={12} md={3}>
                          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                            <CardContent>
                              <Typography variant="h6">📌 Total missions</Typography>
                              <Divider sx={{ my: 1 }} />
                              <Typography><b>{stats.kpis.missionsStats.missionsTotal}</b></Typography>
                            </CardContent>
                          </Card>
                        </Grid>

                        {/* Missions actives */}
                        <Grid item xs={12} md={3}>
                          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                            <CardContent>
                              <Typography variant="h6">🟢 Missions actives</Typography>
                              <Divider sx={{ my: 1 }} />
                              <Typography><b>{stats.kpis.missionsStats.activeMissionsTotal}</b></Typography>
                            </CardContent>
                          </Card>
                        </Grid>

                        {/* Missions urgentes */}
                        <Grid item xs={12} md={3}>
                          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                            <CardContent>
                              <Typography variant="h6">⚡ Missions urgentes</Typography>
                              <Divider sx={{ my: 1 }} />
                              <Typography><b>{stats.kpis.missionsStats.missionUrgentTotal}</b></Typography>
                            </CardContent>
                          </Card>
                        </Grid>

                        {/* Rémunération */}
                        <Grid item xs={12} md={12}>
                          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                            <CardContent>
                              <Typography variant="h6">💰 Rémunération (Moyenne / Médiane)</Typography>
                              <Divider sx={{ my: 1 }} />
                              <Grid container spacing={2}>
                                {Object.entries(stats.kpis.missionsStats.remuneration || {}).map(([devise, data]) => (
                                  <Grid item xs={12} md={4} key={devise}>
                                    <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
                                      <CardContent>
                                        <Typography variant="subtitle1"><b>{devise}</b></Typography>
                                        <Typography>📊 Moyenne : <b>{(data?.moyenne ?? 0).toFixed(0)} {devise}</b></Typography>
                                        <Typography>📈 Médiane : <b>{(data?.mediane ?? 0).toFixed(0)} {devise}</b></Typography>
                                        <Typography>📌 Missions : <b>{data?.nombreMissions ?? 0}</b></Typography>
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                ))}
                              </Grid>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    )}

                    {activeTab === "talent" && (
                      <Grid container spacing={2}>
                        {/* Disponibilités */}
                        <Grid container spacing={2} alignItems="stretch">
                          <Grid item xs={12} md={4}>
                            <Card sx={{ borderRadius: 3, boxShadow: 3, height: "100%" }}>
                              <CardContent>
                                <Typography variant="h6">📅 Disponibilités</Typography>
                                <Divider sx={{ my: 1 }} />
                                {stats.kpis.talentsStats.disponibilites.length ? (
                                  stats.kpis.talentsStats.disponibilites.map((d, i) => (
                                    <Typography key={i}>{d._id} : <b>{d.count}</b></Typography>
                                  ))
                                ) : (
                                  <Typography variant="body2">Aucune donnée.</Typography>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>

                        {/* Expérience */}
                        <Grid container spacing={2} alignItems="stretch">
                          <Grid item xs={12} md={4}>
                            <Card sx={{ borderRadius: 3, boxShadow: 3, height: "100%" }}>
                              <CardContent>
                                <Typography variant="h6">💼 Expérience</Typography>
                                <Divider sx={{ my: 1 }} />
                                {stats.kpis.talentsStats.experience.length ? (
                                  stats.kpis.talentsStats.experience.map((e, i) => {
                                    const label =
                                      e.label !== "Non précisée" ? `${e.label}` : e.label;
                                    return (
                                      <Typography key={i}>
                                        {label} : <b>{e.percentage}</b>
                                      </Typography>
                                    );
                                  })
                                ) : (
                                  <Typography variant="body2">Aucune donnée.</Typography>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>
                      </Grid>
                    )}

                    {activeTab === "resume" && (
                      <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                        <CardContent>
                          <Typography variant="h6">📊 Vue d’ensemble</Typography>
                          <Divider sx={{ my: 1 }} />
                          <Typography>
                            📌 Missions : <b>{stats.kpis.missionsStats.missionsTotal}</b> (dont{" "}
                            {stats.kpis.missionsStats.activeMissionsTotal} actives,{" "}
                            {stats.kpis.missionsStats.missionUrgentTotal} urgentes)
                          </Typography>
                          <Typography>
                            📌 Moyenne candidatures / mission :{" "}
                            <b>{(stats.kpis.candidaturesStats.moyenneParMission ?? 0).toFixed(2)}</b>
                          </Typography>
                          <Typography>
                            📌 Rémunération missions :{" "}
                            {Object.entries(stats.kpis.missionsStats.remuneration || {}).map(([devise, data]) => (
                              <span key={devise}>
                                <b>{devise} {(data?.moyenne ?? 0).toFixed(0)} / {(data?.mediane ?? 0).toFixed(0)}</b>{" "}
                              </span>
                            ))}
                          </Typography>
                        </CardContent>
                      </Card>
                    )}
                  </Box>

                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
        {/* === END TWO-COLUMN LAYOUT === */}
      </Box>
      <Footer />
    </Box>
  );
};

export default StatistiquesPage;
